// ---------------------------------------------------------------------------
// richText/document.ts — the shared HTML document behind the rich-text editor.
//
// One self-contained `contentEditable` document (toolbar + editor + bridge
// script) is built here as a string and then hosted by whichever shell the
// platform can run:
//   - native  → react-native-webview   (../RichTextEditor.tsx)
//   - web     → <iframe srcDoc>        (../RichTextEditor.web.tsx)
//
// Both shells receive the SAME messages: the script posts through
// `window.ReactNativeWebView.postMessage` when that bridge exists and falls
// back to `window.parent.postMessage` inside an iframe, so neither shell
// needs its own copy of the editor logic.
//
// Formatting is driven by `document.execCommand` (still supported by
// WKWebView, Android System WebView and every desktop browser) plus a few
// hand-rolled commands for things execCommand never covered: font size as an
// inline `style="font-size:Npx"` span (the shape tiptap's FontSize extension
// emits, so notes still round-trip with the legacy web editor), table insert
// and the table row/column operations.
// ---------------------------------------------------------------------------

// Base64-encode UTF-8 text for safe injection into the inline <script>
// (sidesteps quote/backtick/script-tag escaping entirely — the document
// decodes it with atob + a UTF-8-safe unescape/decodeURIComponent unwrap).
// No RN `Buffer` global is assumed (Hermes doesn't provide one without an
// extra polyfill dep), so this is a small self-contained encoder built from
// string/charCode primitives only.
const BASE64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function toBase64Utf8(input: string): string {
  const utf8 = encodeURIComponent(input).replace(/%([0-9A-F]{2})/g, (_m, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
  let output = '';
  let i = 0;
  for (; i + 2 < utf8.length; i += 3) {
    const b0 = utf8.charCodeAt(i);
    const b1 = utf8.charCodeAt(i + 1);
    const b2 = utf8.charCodeAt(i + 2);
    output +=
      BASE64_CHARS[b0 >> 2] +
      BASE64_CHARS[((b0 & 3) << 4) | (b1 >> 4)] +
      BASE64_CHARS[((b1 & 15) << 2) | (b2 >> 6)] +
      BASE64_CHARS[b2 & 63];
  }
  const remaining = utf8.length - i;
  if (remaining === 1) {
    const b0 = utf8.charCodeAt(i);
    output += BASE64_CHARS[b0 >> 2] + BASE64_CHARS[(b0 & 3) << 4] + '==';
  } else if (remaining === 2) {
    const b0 = utf8.charCodeAt(i);
    const b1 = utf8.charCodeAt(i + 1);
    output += BASE64_CHARS[b0 >> 2] + BASE64_CHARS[((b0 & 3) << 4) | (b1 >> 4)] + BASE64_CHARS[(b1 & 15) << 2] + '=';
  }
  return output;
}

/**
 * Fields that predate this editor hold PLAIN TEXT (TKNE's `status_update` is
 * the case that prompted it). Feeding that straight into `innerHTML` would
 * collapse every line break, so anything that doesn't already look like
 * markup is escaped and split into paragraphs first. Detection is
 * deliberately loose — a tag-shaped substring is enough — because the only
 * cost of a false positive on genuinely tag-free text is the (correct)
 * plain-text path being skipped.
 */
export function ensureHtml(value: string): string {
  const text = value ?? '';
  if (!text.trim()) return '';
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .split(/\n/)
    .map((line) => `<p>${line.trim() === '' ? '<br>' : line}</p>`)
    .join('');
}

export interface RichTextDocumentOptions {
  /** Initial markup (or plain text — it is normalised by `ensureHtml`). */
  value: string;
  /** Shown in the empty editor. */
  placeholder?: string;
  /** Debounce before a `change` message is posted, ms. */
  debounceMs?: number;
}

const FONT_STACKS: Array<[label: string, css: string]> = [
  ['Default', ''],
  ['Sans', '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'],
  ['Serif', 'Georgia, "Times New Roman", Times, serif'],
  ['Mono', 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'],
];

const SWATCHES = [
  '#22303f', '#5b6b7c', '#c0392b', '#d35400', '#c9a227',
  '#2e8650', '#1f5aa6', '#6f42c1', '#b03a6f', '#ffffff',
  '#f6f07a', '#f7b6d2', '#8de0c9', '#a3e635', '#38bdf8',
  '#4c1d95', '#8b5e34', '#000000', '#c7ced6', '#ff8a65',
];

export function buildRichTextDocument(opts: RichTextDocumentOptions): string {
  const { value, placeholder = 'Start writing…', debounceMs = 400 } = opts;
  const encoded = toBase64Utf8(ensureHtml(value));
  const fontOptions = FONT_STACKS.map(
    ([label, css], i) => `<option value="${css}"${i === 0 ? ' selected' : ''}>${label}</option>`
  ).join('');
  const swatches = SWATCHES.map(
    (hex) => `<button class="swatch" data-color="${hex}" style="background:${hex}"></button>`
  ).join('');

  return `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
<style>
  * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
  html, body { margin: 0; padding: 0; height: 100%; background: #ffffff; }
  body {
    display: flex; flex-direction: column; height: 100%;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  }
  #toolbar {
    display: flex; align-items: center; flex-wrap: wrap; gap: 3px;
    padding: 5px 6px; background: #f4f6f8; border-bottom: 1px solid #dbe2ea;
    flex-shrink: 0; position: relative;
  }
  #toolbar button {
    border: 1px solid #dbe2ea; background: #ffffff; border-radius: 6px;
    min-width: 28px; height: 28px; font-size: 13px; font-weight: 700;
    color: #22303f; padding: 0 6px; cursor: pointer; line-height: 1;
  }
  #toolbar button:active { background: #e8eef4; }
  #toolbar button.active { background: #d3f36b; border-color: #d3f36b; color: #1f5aa6; }
  #toolbar select, #toolbar input[type="number"] {
    height: 28px; border: 1px solid #dbe2ea; border-radius: 6px;
    background: #ffffff; color: #22303f; font-size: 12px; padding: 0 4px;
  }
  #fontSize { width: 46px; }
  #blockStyle { width: 92px; }
  #fontFamily { width: 84px; }
  #toolbar .sep { width: 1px; height: 18px; background: #dbe2ea; margin: 0 2px; }
  .popover {
    position: absolute; top: 100%; left: 6px; z-index: 20; display: none;
    background: #ffffff; border: 1px solid #dbe2ea; border-radius: 8px;
    padding: 8px; box-shadow: 0 6px 18px rgba(20,32,45,0.18);
  }
  .popover.open { display: block; }
  .swatches { display: grid; grid-template-columns: repeat(5, 22px); gap: 6px; }
  .swatch { width: 22px; height: 22px; border-radius: 5px; border: 1px solid #c3ced9; padding: 0; }
  .popover .row { display: flex; gap: 6px; margin-top: 8px; }
  .popover input[type="text"] {
    height: 28px; border: 1px solid #dbe2ea; border-radius: 6px; padding: 0 8px;
    font-size: 13px; min-width: 200px;
  }
  .popover .customColorRow { align-items: center; }
  .popover .customColorRow label { font-size: 12px; color: #5b6b7c; }
  .popover input[type="color"] {
    width: 28px; height: 28px; padding: 0; border: 1px solid #c3ced9;
    border-radius: 6px; background: none; cursor: pointer;
  }
  #editor {
    flex: 1; overflow-y: auto; padding: 12px 14px; font-size: 15px;
    line-height: 1.5; color: #22303f; outline: none; word-wrap: break-word;
  }
  #editor ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
  #editor ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
  #editor li { margin: 0.25em 0; }
  #editor table { width: 100%; border-collapse: collapse; border: 1px solid #a9cbe8; table-layout: fixed; margin: 1em 0; }
  #editor th, #editor td { border: 1px solid #a9cbe8; padding: 6px 10px; text-align: left; vertical-align: top; }
  #editor th { background: #cfe6f5; font-weight: 600; }
  #editor p { margin: 0 0 0.6em 0; }
  #editor h1 { font-size: 1.7em; margin: 0.5em 0 0.3em; }
  #editor h2 { font-size: 1.4em; margin: 0.5em 0 0.3em; }
  #editor h3 { font-size: 1.2em; margin: 0.5em 0 0.3em; }
  #editor blockquote {
    margin: 0.6em 0; padding: 2px 0 2px 12px;
    border-left: 3px solid #a9cbe8; color: #4a6d8c;
  }
  #editor pre {
    background: #eef2f6; border-radius: 6px; padding: 10px 12px; overflow-x: auto;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 13px;
  }
  #editor hr { border: none; border-top: 1px solid #c3ced9; margin: 1em 0; }
  #editor a { color: #1f5aa6; }
  #editor img { max-width: 100%; }
  #editor:empty:before {
    content: attr(data-placeholder); color: #9aa7b4; pointer-events: none;
  }
</style>
</head>
<body>
  <div id="toolbar">
    <button id="btn-undo" data-cmd="undo" title="Undo">&#8630;</button>
    <button id="btn-redo" data-cmd="redo" title="Redo">&#8631;</button>
    <div class="sep"></div>
    <select id="blockStyle" title="Paragraph style">
      <option value="p">Normal</option>
      <option value="h1">Heading 1</option>
      <option value="h2">Heading 2</option>
      <option value="h3">Heading 3</option>
      <option value="blockquote">Quote</option>
      <option value="pre">Code block</option>
    </select>
    <select id="fontFamily" title="Font">${fontOptions}</select>
    <input id="fontSize" type="number" min="8" max="96" placeholder="px" title="Font size" />
    <div class="sep"></div>
    <button id="btn-bold" data-cmd="bold" title="Bold"><b>B</b></button>
    <button id="btn-italic" data-cmd="italic" title="Italic"><i>I</i></button>
    <button id="btn-underline" data-cmd="underline" title="Underline"><u>U</u></button>
    <button id="btn-strike" data-cmd="strikeThrough" title="Strikethrough"><span style="text-decoration:line-through">S</span></button>
    <button id="btn-sup" data-cmd="superscript" title="Superscript">x&sup2;</button>
    <button id="btn-sub" data-cmd="subscript" title="Subscript">x&#8322;</button>
    <div class="sep"></div>
    <button id="btn-color" title="Text colour"><span style="color:#c0392b">A</span></button>
    <button id="btn-highlight" title="Highlight"><span style="background:#f6f07a;padding:0 3px">A</span></button>
    <div class="sep"></div>
    <button id="btn-ul" data-cmd="insertUnorderedList" title="Bulleted list">&#8226;&#8801;</button>
    <button id="btn-ol" data-cmd="insertOrderedList" title="Numbered list">1&#8801;</button>
    <button id="btn-outdent" data-cmd="outdent" title="Decrease indent">&#8676;</button>
    <button id="btn-indent" data-cmd="indent" title="Increase indent">&#8677;</button>
    <div class="sep"></div>
    <button id="btn-alignLeft" data-cmd="justifyLeft" title="Align left">&#8801;</button>
    <button id="btn-alignCenter" data-cmd="justifyCenter" title="Centre">&#8803;</button>
    <button id="btn-alignRight" data-cmd="justifyRight" title="Align right">&#8802;</button>
    <button id="btn-alignJustify" data-cmd="justifyFull" title="Justify">&#9776;</button>
    <div class="sep"></div>
    <button id="btn-link" title="Insert link">&#128279;</button>
    <button id="btn-unlink" data-cmd="unlink" title="Remove link">&#128279;&#10005;</button>
    <button id="btn-hr" title="Horizontal rule">&#8213;</button>
    <div class="sep"></div>
    <button id="btn-table" title="Insert table">&#9638; Table</button>
    <button id="btn-rowAbove" title="Row above">&#8593;R</button>
    <button id="btn-rowBelow" title="Row below">&#8595;R</button>
    <button id="btn-colLeft" title="Column left">&#8592;C</button>
    <button id="btn-colRight" title="Column right">&#8594;C</button>
    <button id="btn-delRow" title="Delete row">&#10005;R</button>
    <button id="btn-delCol" title="Delete column">&#10005;C</button>
    <button id="btn-delTable" title="Delete table">&#10005;&#9638;</button>
    <div class="sep"></div>
    <button id="btn-clear" data-cmd="removeFormat" title="Clear formatting">T&#10005;</button>

    <div class="popover" id="colorPop">
      <div class="swatches" data-target="fore">${swatches}</div>
    </div>
    <div class="popover" id="highlightPop">
      <div class="swatches" data-target="back">${swatches}</div>
      <div class="row customColorRow">
        <label for="customHighlight">Custom</label>
        <input id="customHighlight" type="color" value="#f6f07a" />
      </div>
    </div>
    <div class="popover" id="linkPop">
      <div class="row">
        <input id="linkUrl" type="text" placeholder="https://…" />
        <button id="linkApply">Apply</button>
      </div>
    </div>
  </div>
  <div id="editor" contenteditable="true" spellcheck="true" data-placeholder="${placeholder.replace(/"/g, '&quot;')}"></div>

<script>
(function () {
  var editor = document.getElementById('editor');
  var RN = window.ReactNativeWebView;

  // One bridge, two shells: the RN WebView injects ReactNativeWebView, an
  // iframe gets the same JSON string through postMessage to its parent.
  function post(type, payload) {
    var msg = JSON.stringify(Object.assign({ source: 'syqi-rich-text', type: type }, payload || {}));
    if (RN && RN.postMessage) RN.postMessage(msg);
    else if (window.parent && window.parent !== window) window.parent.postMessage(msg, '*');
  }

  try {
    editor.innerHTML = decodeURIComponent(escape(window.atob("${encoded}")));
  } catch (e) {
    editor.innerHTML = '';
  }

  // execCommand's own undo stack covers the built-in commands; styleWithCSS
  // keeps colour/size marks as inline styles instead of legacy <font> tags.
  try { document.execCommand('styleWithCSS', false, true); } catch (e) {}

  var saveTimer = null;
  function scheduleChange() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(function () { post('change', { html: editor.innerHTML }); }, ${debounceMs});
  }
  function flush() {
    if (saveTimer) clearTimeout(saveTimer);
    post('change', { html: editor.innerHTML });
  }

  editor.addEventListener('input', scheduleChange);
  editor.addEventListener('blur', flush);

  function run(cmd, arg) {
    editor.focus();
    try { document.execCommand(cmd, false, arg === undefined ? null : arg); } catch (e) {}
    scheduleChange();
    updateToolbarState();
  }

  // --- selection bookkeeping. Clicking a toolbar control can drop the
  // editor selection (notably in the popovers, which contain focusable
  // elements), so the last in-editor range is remembered and restored
  // before a command runs.
  var savedRange = null;
  document.addEventListener('selectionchange', function () {
    var sel = window.getSelection();
    if (sel && sel.rangeCount && editor.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      savedRange = sel.getRangeAt(0).cloneRange();
    }
    updateToolbarState();
  });
  function restoreSelection() {
    if (!savedRange) return;
    var sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedRange);
  }

  // Toolbar controls must not steal focus from the editor.
  document.getElementById('toolbar').addEventListener('mousedown', function (e) {
    if (e.target.tagName !== 'SELECT' && e.target.tagName !== 'INPUT') e.preventDefault();
  });

  Array.prototype.forEach.call(document.querySelectorAll('#toolbar button[data-cmd]'), function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      restoreSelection();
      run(btn.getAttribute('data-cmd'));
    });
  });

  // --- block style -------------------------------------------------------
  var blockStyle = document.getElementById('blockStyle');
  blockStyle.addEventListener('change', function () {
    restoreSelection();
    run('formatBlock', '<' + blockStyle.value + '>');
  });

  // --- font family / size ------------------------------------------------
  function wrapSelection(styleName, styleValue) {
    restoreSelection();
    editor.focus();
    var sel = window.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed) return;
    var range = sel.getRangeAt(0);
    var span = document.createElement('span');
    span.style[styleName] = styleValue;
    try {
      range.surroundContents(span);
    } catch (err) {
      // Range crosses element boundaries — extract and re-insert instead.
      var frag = range.extractContents();
      span.appendChild(frag);
      range.insertNode(span);
    }
    scheduleChange();
  }

  document.getElementById('fontFamily').addEventListener('change', function () {
    var v = this.value;
    if (!v) { restoreSelection(); run('removeFormat'); return; }
    wrapSelection('fontFamily', v);
  });

  var fontSizeInput = document.getElementById('fontSize');
  fontSizeInput.addEventListener('change', function () {
    if (!fontSizeInput.value) return;
    wrapSelection('fontSize', fontSizeInput.value + 'px');
  });

  // --- popovers (colour, highlight, link) --------------------------------
  var pops = ['colorPop', 'highlightPop', 'linkPop'];
  function openPop(id) {
    pops.forEach(function (p) {
      document.getElementById(p).classList.toggle('open', p === id && !document.getElementById(p).classList.contains('open'));
    });
  }
  function closePops() { pops.forEach(function (p) { document.getElementById(p).classList.remove('open'); }); }

  document.getElementById('btn-color').addEventListener('click', function (e) { e.preventDefault(); openPop('colorPop'); });
  document.getElementById('btn-highlight').addEventListener('click', function (e) { e.preventDefault(); openPop('highlightPop'); });

  Array.prototype.forEach.call(document.querySelectorAll('.swatch'), function (sw) {
    sw.addEventListener('click', function (e) {
      e.preventDefault();
      var target = sw.parentNode.getAttribute('data-target');
      restoreSelection();
      run(target === 'fore' ? 'foreColor' : 'hiliteColor', sw.getAttribute('data-color'));
      closePops();
    });
  });

  var customHighlight = document.getElementById('customHighlight');
  customHighlight.addEventListener('change', function () {
    restoreSelection();
    run('hiliteColor', customHighlight.value);
    closePops();
  });

  document.getElementById('btn-link').addEventListener('click', function (e) {
    e.preventDefault();
    openPop('linkPop');
    setTimeout(function () { document.getElementById('linkUrl').focus(); }, 0);
  });
  document.getElementById('linkApply').addEventListener('click', function (e) {
    e.preventDefault();
    var url = document.getElementById('linkUrl').value.trim();
    closePops();
    if (!url) return;
    restoreSelection();
    run('createLink', url);
    document.getElementById('linkUrl').value = '';
  });

  document.getElementById('btn-hr').addEventListener('click', function (e) {
    e.preventDefault();
    restoreSelection();
    run('insertHorizontalRule');
  });

  // --- tables ------------------------------------------------------------
  function currentCell() {
    var sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    var node = sel.getRangeAt(0).startContainer;
    while (node && node !== editor) {
      if (node.nodeType === 1 && (node.tagName === 'TD' || node.tagName === 'TH')) return node;
      node = node.parentNode;
    }
    return null;
  }
  function newCell(like) {
    var el = document.createElement(like && like.tagName === 'TH' ? 'th' : 'td');
    el.innerHTML = '&nbsp;';
    return el;
  }

  document.getElementById('btn-table').addEventListener('click', function (e) {
    e.preventDefault();
    restoreSelection();
    editor.focus();
    var html = '<table><tbody>';
    for (var r = 0; r < 3; r++) {
      html += '<tr>';
      for (var c = 0; c < 3; c++) html += r === 0 ? '<th>&nbsp;</th>' : '<td>&nbsp;</td>';
      html += '</tr>';
    }
    html += '</tbody></table><p><br></p>';
    run('insertHTML', html);
  });

  function addRow(below) {
    var cell = currentCell();
    if (!cell) return;
    var row = cell.parentNode;
    var fresh = document.createElement('tr');
    for (var i = 0; i < row.children.length; i++) fresh.appendChild(newCell(row.children[i].tagName === 'TH' ? null : row.children[i]));
    row.parentNode.insertBefore(fresh, below ? row.nextSibling : row);
    scheduleChange();
  }
  function addCol(right) {
    var cell = currentCell();
    if (!cell) return;
    var idx = Array.prototype.indexOf.call(cell.parentNode.children, cell);
    var table = cell.closest('table');
    Array.prototype.forEach.call(table.rows, function (row) {
      var ref = row.children[idx];
      var fresh = newCell(ref);
      row.insertBefore(fresh, right ? (ref ? ref.nextSibling : null) : ref);
    });
    scheduleChange();
  }
  document.getElementById('btn-rowAbove').addEventListener('click', function (e) { e.preventDefault(); addRow(false); });
  document.getElementById('btn-rowBelow').addEventListener('click', function (e) { e.preventDefault(); addRow(true); });
  document.getElementById('btn-colLeft').addEventListener('click', function (e) { e.preventDefault(); addCol(false); });
  document.getElementById('btn-colRight').addEventListener('click', function (e) { e.preventDefault(); addCol(true); });
  document.getElementById('btn-delRow').addEventListener('click', function (e) {
    e.preventDefault();
    var cell = currentCell();
    if (!cell) return;
    var row = cell.parentNode;
    var table = cell.closest('table');
    if (table.rows.length <= 1) { table.remove(); } else { row.remove(); }
    scheduleChange();
  });
  document.getElementById('btn-delCol').addEventListener('click', function (e) {
    e.preventDefault();
    var cell = currentCell();
    if (!cell) return;
    var idx = Array.prototype.indexOf.call(cell.parentNode.children, cell);
    var table = cell.closest('table');
    if (table.rows[0] && table.rows[0].children.length <= 1) { table.remove(); }
    else Array.prototype.forEach.call(table.rows, function (row) { if (row.children[idx]) row.children[idx].remove(); });
    scheduleChange();
  });
  document.getElementById('btn-delTable').addEventListener('click', function (e) {
    e.preventDefault();
    var cell = currentCell();
    if (!cell) return;
    cell.closest('table').remove();
    scheduleChange();
  });

  // --- toolbar state -----------------------------------------------------
  var STATE_MAP = {
    'btn-bold': 'bold', 'btn-italic': 'italic', 'btn-underline': 'underline',
    'btn-strike': 'strikeThrough', 'btn-sup': 'superscript', 'btn-sub': 'subscript',
    'btn-ul': 'insertUnorderedList', 'btn-ol': 'insertOrderedList',
    'btn-alignLeft': 'justifyLeft', 'btn-alignCenter': 'justifyCenter',
    'btn-alignRight': 'justifyRight', 'btn-alignJustify': 'justifyFull'
  };
  function updateToolbarState() {
    Object.keys(STATE_MAP).forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      var active = false;
      try { active = document.queryCommandState(STATE_MAP[id]); } catch (e) {}
      el.classList.toggle('active', !!active);
    });
    var block = '';
    try { block = (document.queryCommandValue('formatBlock') || '').toLowerCase(); } catch (e) {}
    if (block) {
      var opt = blockStyle.querySelector('option[value="' + block + '"]');
      blockStyle.value = opt ? block : 'p';
    }
  }

  editor.addEventListener('click', closePops);
  editor.addEventListener('keydown', function (e) {
    // Tab inside a table walks to the next cell instead of leaving the field.
    if (e.key === 'Tab') {
      var cell = currentCell();
      if (cell) {
        e.preventDefault();
        var cells = Array.prototype.slice.call(cell.closest('table').querySelectorAll('th,td'));
        var next = cells[cells.indexOf(cell) + (e.shiftKey ? -1 : 1)];
        if (next) {
          var range = document.createRange();
          range.selectNodeContents(next);
          range.collapse(true);
          var sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    }
  });

  // Paste as plain text so pasted Word/browser markup can't smuggle in
  // styles (or scripts) the editor never emits itself.
  editor.addEventListener('paste', function (e) {
    e.preventDefault();
    var text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
  });

  post('ready', {});
})();
</script>
</body>
</html>`;
}
